/**
 * Date/time helpers for session display.
 * Stored times are naive-UTC (the API's storage convention); every clock
 * time is rendered in the viewer's own browser timezone and carries a
 * timezone label so it is never ambiguous when shared.
 */

/**
 * Parse an API date ("2026-04-23") + time ("14:00:00" or "14:00") into a Date.
 * Returns null if either input is falsy or the result is invalid.
 */
export function parseDateTime(date: string, time: string): Date | null {
  if (!date || !time) return null;
  const trimmed = time.trim();
  const hasTimezone = /([zZ]|[+\-]\d\d:?\d\d)$/.test(trimmed);
  const iso = hasTimezone ? `${date}T${trimmed}` : `${date}T${trimmed}Z`;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * "Today · 12:00 PST" / "Tomorrow · 9:00 PST" / "Apr 24 · 11:30 PST"
 * Day and time are in the viewer's browser timezone, always tz-labeled.
 */
export function formatWhen(date: string, startTime: string): string {
  const dt = parseDateTime(date, startTime);
  if (!dt) return "N/A";

  const timeFmt = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: false,
    timeZoneName: "short",
  });

  const now = new Date();
  const dtKey = browserDateKey(dt);
  const dateStr = dtKey === browserDateKey(now)
    ? "Today"
    : dtKey === browserDateKey(addDays(now, 1))
      ? "Tomorrow"
      : new Intl.DateTimeFormat("en-US", {
          month: "short",
          day: "numeric",
        }).format(dt);

  return `${dateStr} · ${timeFmt.format(dt)}`;
}

/**
 * "In 2 hours" / "Tomorrow" / "Yesterday" / "3 days ago"
 */
export function formatRelative(date: string, startTime: string): string {
  const dt = parseDateTime(date, startTime);
  if (!dt) return "";

  const now = new Date();
  const diffMs = dt.getTime() - now.getTime();
  const diffHours = diffMs / (1000 * 60 * 60);
  const diffDays = diffMs / (1000 * 60 * 60 * 24);

  if (diffMs > 0) {
    if (diffHours < 1) {
      const mins = Math.round(diffMs / (1000 * 60));
      return `In ${mins} min`;
    }
    if (diffHours < 24) {
      const hrs = Math.round(diffHours);
      return `In ${hrs} hour${hrs !== 1 ? "s" : ""}`;
    }
    if (diffDays < 2) return "Tomorrow";
    const days = Math.round(diffDays);
    return `In ${days} days`;
  }

  const absDays = Math.abs(diffDays);
  if (absDays < 1) return "Today";
  if (absDays < 2) return "Yesterday";
  const days = Math.round(absDays);
  return `${days} days ago`;
}

/**
 * "12:00 – 1:30 PM PDT" in the viewer's browser timezone. The timezone
 * short name appears once, on the end time, so it reads naturally.
 */
export function formatTimeRange(
  date: string,
  startTime: string,
  endTime: string,
): string {
  const start = parseDateTime(date, startTime);
  const end = parseDateTime(date, endTime);
  if (!start || !end) return "N/A";

  const timeFmt = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });

  const tzFmt = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  });

  return `${timeFmt.format(start)} – ${tzFmt.format(end)}`;
}

/**
 * "90 minutes"
 */
export function formatDuration(startTime: string, endTime: string, date = "2000-01-01"): string {
  const start = parseDateTime(date, startTime);
  const end = parseDateTime(date, endTime);
  if (!start || !end) return "N/A";

  const mins = Math.round((end.getTime() - start.getTime()) / (1000 * 60));
  return `${mins} minute${mins !== 1 ? "s" : ""}`;
}

/**
 * Format an ISO date string into a human-readable date.
 * "Thu, Apr 23, 2026"
 */
export function formatDateLabel(dateStr: string): string {
  // Robust solution: Parse date and format in UTC to avoid timezone shifts
  const [y, m, d] = dateStr.split("-").map(Number);
  const date = new Date(Date.UTC(y, m - 1, d, 12)); // noon UTC, safe from any TZ shift
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    weekday: "long",
    month: "long",
    day: "numeric",
  }).format(date);
}

/**
 * "Mon, Apr 20" style date for availability picker.
 */
export function formatShortDate(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00");
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  return `${days[d.getDay()]}, ${months[d.getMonth()]} ${d.getDate()}`;
}

// ── Browser-local formatting ─────────────────────────────────────────
// Stored session times are naive-UTC wall-clock (the API's convention,
// matched by the public booking site and confirmation emails). These
// helpers convert a stored date+time into the *viewer's* timezone so the
// dashboard shows times the way the practitioner actually experiences them.

/**
 * Browser-local time, 24-hour, no leading hour zero. e.g. "9:00" / "17:00".
 */
export function formatLocalTime(date: string, startTime: string): string {
  const dt = parseDateTime(date, startTime);
  if (!dt) return "N/A";
  return new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(dt);
}

/**
 * Browser-local day label. e.g. "Mon, Jun 15".
 */
export function formatLocalDay(date: string, startTime: string): string {
  const dt = parseDateTime(date, startTime);
  if (!dt) return "";
  return new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  }).format(dt);
}

/**
 * Browser timezone abbreviation for a given instant. e.g. "PST" / "EDT".
 * Computed per-instant so it stays correct across DST boundaries.
 */
export function timeZoneAbbr(d: Date): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    timeZoneName: "short",
  }).formatToParts(d);
  return parts.find((p) => p.type === "timeZoneName")?.value ?? "";
}

/**
 * Browser timezone abbreviation for a stored date+time. e.g. "PST" / "EDT".
 */
export function browserTimeZoneAbbr(date: string, startTime: string): string {
  const dt = parseDateTime(date, startTime);
  return dt ? timeZoneAbbr(dt) : "";
}

/**
 * Browser-local YYYY-MM-DD key for grouping sessions by day. The local date
 * can differ from the stored UTC date once converted, so group on this.
 */
export function localDateKey(date: string, startTime: string): string {
  const dt = parseDateTime(date, startTime);
  return dt ? browserDateKey(dt) : date;
}

/**
 * Today's date as a browser-local YYYY-MM-DD key.
 */
export function localTodayKey(): string {
  return browserDateKey(new Date());
}

/**
 * Method to location label for display.
 */
export function methodToLocation(method: string): string {
  if (method === "in-person") return "Office";
  if (method === "video") return "Video";
  if (method === "phone") return "Phone";
  return method;
}

// ── Internal helpers ─────────────────────────────────────────────────

function browserDateKey(d: Date): string {
  // YYYY-MM-DD in the viewer's browser timezone.
  const parts = new Intl.DateTimeFormat("en-CA").formatToParts(d);
  const y = parts.find((p) => p.type === "year")!.value;
  const m = parts.find((p) => p.type === "month")!.value;
  const day = parts.find((p) => p.type === "day")!.value;
  return `${y}-${m}-${day}`;
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

/**
 * Human-readable file size.
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Format an ISO date string as a relative time label.
 * "just now" / "3m ago" / "2h ago" / "3d ago"
 */
export function timeAgo(isoDate: string): string {
  const d = new Date(isoDate);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const mins = Math.floor(diffMs / (1000 * 60));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}
